import path from 'node:path'
import matter from 'gray-matter'

import type {
  LoadableContent,
  NavigationRecord,
  PathLoadableContent,
  ResolvedPage,
  ScalarConfig,
} from '@/schema'
import type {
  InternalOpenApiNavigationEntry,
  InternalOpenapiNavigationEntryLiveLink,
  InternalOpenapiNavigationEntryPath,
  InternalOpenapiNavigationEntryRegistrySchema,
} from '@/schema/navigation/openapi'
import type { InternalPageNavigationEntry } from '@/schema/navigation/pages'
import type { ProcessedPage, ProcessedResolvedPage } from '@/schema/processing'
import type { PageMeta } from '@/types'

/** Check if a page has a valid file extension */
export function isValidPageFile(filepath: string) {
  const ext = filepath.split('.').at(-1)
  return !!ext && ['md', 'mdx', 'jsx', 'tsx', 'html', 'htm'].includes(ext)
}

export function isPathLoadableContent(
  loadableContent: LoadableContent,
): loadableContent is PathLoadableContent {
  return 'path' in loadableContent
}

/**
 * Extract the page title and description from the page  content.
 *
 * Priority is given to H1 tags and page frontmatter
 */
export function getPageMeta(markdown: string) {
  const { data: meta, content } = matter(markdown) as {
    data: PageMeta
    content: string
  }

  /** If there is an H1 tag we use the first one found for the page title */
  if (!meta.title) {
    // Regex to match all level 1 headings
    const h1Match = /^#(?!#)(.*)/gm
    /** Grab the first 3000 characters to avoid regex performance issues */
    const titleResult = [...markdown.slice(0, 3000).matchAll(h1Match)]
    meta.title = titleResult?.[0]?.[1]?.trim?.()
  }

  return {
    meta,
    content,
  }
}

/**
 * Returns a pretty filename from a path string
 *
 * - Remove path slashes
 * - Remove file extension
 * - Replace dashes and underscores with spaces
 */
export function prettyFilename(filename: string): string {
  const name =
    filename
      .split('/')
      .at(-1)
      ?.split('.')[0]
      .replaceAll('-', ' ')
      .replaceAll('_', ' ') ?? ''

  /**
   * If the name is empty then name[0]
   */
  return name[0] ? name[0].toUpperCase() + name.slice(1) : name
}

/** Merge multiple objects into a single object */
export function merge<T>(sources: T[]): T {
  return Object.assign({}, ...sources)
}

export function mergeArrays<T>(arrays: ReadonlyArray<ReadonlyArray<T>>): T[] {
  return [...arrays].flat()
}

export function mergePaths(...paths: string[]): string {
  return path.posix.normalize(path.posix.join('/', ...paths))
}

export const isRegistryOpenapiPage = (
  page: ResolvedPage,
): page is InternalOpenapiNavigationEntryRegistrySchema =>
  page.type === 'openapi' &&
  'slug' in page &&
  'apiVersion' in page &&
  'namespace' in page

export const isLocalOpenapiPage = (
  page: ResolvedPage,
): page is InternalOpenapiNavigationEntryPath =>
  page.type === 'openapi' && 'filepath' in page

export const isLiveLinkOpenapiPage = (
  page: ResolvedPage,
): page is InternalOpenapiNavigationEntryLiveLink =>
  page.type === 'openapi' && 'url' in page

export const isOpenapiPage = (
  page: ResolvedPage,
): page is InternalOpenApiNavigationEntry => page.type === 'openapi'
export const isHtmlPage = (
  page: ResolvedPage,
): page is InternalPageNavigationEntry =>
  page.type === 'page' && page.filepath.endsWith('.html')
export const isYjsPage = (page: ResolvedPage) =>
  'filepath' in page && page.filepath.endsWith('.yjs')
export const isMarkdownPage = (
  page: ResolvedPage,
): page is InternalPageNavigationEntry =>
  page.type === 'page' && !page.isMdx && !isHtmlPage(page)
export const isMdxPage = (page: ProcessedResolvedPage): page is ProcessedPage =>
  page.type === 'page' && page.isMdx

/**
 * Is the path an index path
 *
 * Index paths are determined by whether the path ends with a trailing slash
 */
export function isIndexPath(path: string) {
  return path.endsWith('/')
}

/**
 * Fully resolves a project directory with a config path and
 * an optional project root
 */
export function resolveProjectDir(
  {
    projectDir,
    configPath,
    root,
  }: {
    projectDir: string
    configPath: string
    root?: string
  },
  ...paths: string[]
) {
  return path.resolve(
    path.join(projectDir, path.dirname(configPath), root ?? '.', ...paths),
  )
}

/** Get a list of all registry APIs in a navigation record */
export function getNavigationRegistryApis(
  navigation: NavigationRecord,
  apis: { namespace: string; slug: string; version: string }[] = [],
) {
  Object.entries(navigation).forEach(([_, nav]) => {
    if (typeof nav === 'string') {
      return
    }

    if (nav.type === 'openapi' && 'namespace' in nav) {
      apis.push({
        namespace: nav.namespace,
        slug: nav.slug,
        version: nav.version,
      })
    }

    if (nav.type === 'group') {
      getNavigationRegistryApis(nav.children, apis)
    }
  })

  return apis
}

/** Get a list of all registry APIs in a Scalar Config */
export function getConfigRegistryApis(config: ScalarConfig) {
  if ('navigation' in config) {
    return getNavigationRegistryApis(config.navigation.routes)
  }

  return Object.values(config.versions).flatMap((version) =>
    getNavigationRegistryApis(version.routes),
  )
}
